import * as R from 'ramda';
import { Observable, combineLatest, map } from 'rxjs';
import { LiquidityAmount, Token } from '@akropolis-web/primitives';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  TotalBalance,
  FallbackedTotalUSD,
  LendingTokenMetrics,
  Rating,
  ProductSlug,
  VersionSlug,
  NetworkSlug,
} from 'domain/types';
import { getAmount, ERROR_MESSAGES, hasSignificantAmounts, hasUnknownPrice } from 'domain/utils';
import { compareAmounts } from 'utils/amount';
import { toEither, right, left, combine, Either } from 'utils/either';
import { isEqualContracts } from 'utils/validators';
import { calcFallbackedTotalUsd } from 'api/utils/calcFallbackedTotalUSD';
import { RouteUtils } from 'routes/types';

export type ProtocolAssetsTabData = {
  shouldRenderActionsColumn: boolean;
  tableData: AssetsTableData[];
};

export type AssetsTableData = {
  rating: Either<Rating>;
  token: Token;
  price: Either<number>;
  collateral: ColumnData;
  collateralDiff24H: ColumnData;
  borrow: ColumnData;
  borrowDiff24H: ColumnData;
  marketHref: string | null;
  lowSupply: boolean;
};

type ColumnData = {
  amount: FallbackedTotalUSD;
  progress: {
    value: LiquidityAmount;
    total: LiquidityAmount;
  } | null;
};

export function getProtocolAssetsTabData({
  productSlug,
  versionSlug,
  network,
  routeRedirectTo,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  routeRedirectTo: RouteUtils['redirectTo'];
}): Observable<ProtocolAssetsTabData> {
  return combineLatest([
    protocolsApi.getProtocolMetrics$('latest', {
      productSlug,
      versionSlug,
      network,
    }),
    protocolsApi.getTokensMetrics$('latest', { productSlug, versionSlug, network }),
    protocolsApi.getTokensMetrics$('dayAgo', { productSlug, versionSlug, network }),
    ratingsApi.getAssetsRatings$({ productSlug, versionSlug, network }),
  ]).pipe(
    map(([protocolMetrics, tokensMetrics, tokensMetricsDayAgo, assetsRatings]) => {
      const linksToMarkets = tokensMetrics.map(tokenMetrics => {
        const marketIds = R.pluck('marketId', tokenMetrics.assetTotals);
        return marketIds
          .filter(id => id !== 'ID' && id !== 'main')
          .map(
            id =>
              routeRedirectTo.marketAssets({
                productSlug: protocolMetrics.productSlug,
                versionSlug,
                network,
                marketId: id,
              }).href,
          );
      });

      const shouldRenderActionsColumn = linksToMarkets.some(links => links.length === 1);

      const metricsDayAgo = tokensMetrics.map(tokenMetrics =>
        tokensMetricsDayAgo.find(x => isEqualContracts(x.token, tokenMetrics.token)),
      );

      const totalCollaterals: FallbackedTotalUSD[] = tokensMetrics.map(tokenMetrics =>
        calcFallbackedTotalUsd(tokenMetrics.assetTotals, 'totalCollateral'),
      );

      const collateralDiffs = tokensMetrics.map((tokenMetrics, index) =>
        calculateDiffAmount(tokenMetrics, metricsDayAgo[index], 'totalCollateral'),
      );
      const [collateralDiffProgressTotal] = collateralDiffs
        .map(d =>
          toEither(d.value).fold(
            x => x.withValue(x.abs()),
            () => getAmount(0, '$'),
          ),
        )
        .sort(compareAmounts);

      const totalDebts: FallbackedTotalUSD[] = tokensMetrics.map(tokenMetrics =>
        calcFallbackedTotalUsd(tokenMetrics.assetTotals, 'totalDebt'),
      );

      const debtDiffs = tokensMetrics.map((tokenMetrics, index) =>
        calculateDiffAmount(tokenMetrics, metricsDayAgo[index], 'totalDebt'),
      );
      const [debtDiffProgressTotal] = debtDiffs
        .map(d =>
          toEither(d.value).fold(
            x => x.withValue(x.abs()),
            () => getAmount(0, '$'),
          ),
        )
        .sort(compareAmounts);

      const props: ProtocolAssetsTabData = {
        shouldRenderActionsColumn,
        tableData: tokensMetrics.map((tokenMetrics, index) => {
          const assetRating = assetsRatings.get(tokenMetrics.token.address);
          const rating = assetRating
            ? right(assetRating.rating)
            : left(ERROR_MESSAGES.UNKNOWN_RATING);

          const data: AssetsTableData = {
            rating,
            token: tokenMetrics.token,
            price: tokenMetrics.price.map(p => p.price),
            collateral: {
              amount: totalCollaterals[index],
              progress: toEither(totalCollaterals[index].value).fold(
                value => ({
                  value,
                  total: protocolMetrics.partialBalanceUSD.totalCollateral,
                }),
                () => null,
              ),
            },
            collateralDiff24H: {
              amount: collateralDiffs[index],
              progress: toEither(collateralDiffs[index].value).fold(
                value => ({
                  value,
                  total: collateralDiffProgressTotal,
                }),
                () => null,
              ),
            },
            borrow: {
              amount: totalDebts[index],
              progress: toEither(totalDebts[index].value).fold(
                value => ({
                  value,
                  total: protocolMetrics.partialBalanceUSD.totalDebt,
                }),
                () => null,
              ),
            },
            borrowDiff24H: {
              amount: debtDiffs[index],
              progress: toEither(debtDiffs[index].value).fold(
                value => ({
                  value,
                  total: debtDiffProgressTotal,
                }),
                () => null,
              ),
            },
            lowSupply: tokenMetrics.assetTotals.every(m => !hasSignificantAmounts(m.balance)),
            marketHref: linksToMarkets[index].length === 1 ? linksToMarkets[index][0] : null,
          };
          return data;
        }),
      };
      return props;
    }),
  );
}

function calculateDiffAmount(
  tokenMetrics: LendingTokenMetrics,
  metricsDayAgo: LendingTokenMetrics | undefined,
  type: keyof TotalBalance,
): FallbackedTotalUSD {
  if (!metricsDayAgo) {
    return {
      value: getAmount(0, '$'),
      fallback: {
        partialTotalUSD: getAmount(0, '$'),
        amountsWithPrice: [],
        amountsWithoutPrice: [tokenMetrics.balance[type].withValue(0)],
      },
    };
  }
  return {
    value: hasUnknownPrice(tokenMetrics.assetTotals)
      ? left(ERROR_MESSAGES.NOT_ALL_TOKENS_HAS_PRICE)
      : combine(tokenMetrics.balanceUSD, metricsDayAgo.balanceUSD).map(([tMetrics, daMetrics]) =>
          tMetrics[type].sub(daMetrics[type]),
        ),
    fallback: {
      partialTotalUSD: getAmount(0, '$'),
      amountsWithPrice: [],
      amountsWithoutPrice: [tokenMetrics.balance[type].sub(metricsDayAgo.balance[type])],
    },
  };
}
